import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FilePlus2 } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import {
  ADMIN_PROSPECT_LETTER_TEMPLATE_NAME_MAX,
  buildCreateAdminProspectLetterTemplateRichRequest,
  buildGetAdminProspectLetterTemplateAssetPreviewUrlRequest,
  createAdminProspectLetterTemplate,
  createAdminProspectLetterTemplateWriteAttempt,
  getAdminProspectLetterTemplateAssetPreviewUrl,
} from '../../services/admin/prospectLetterTemplatesService.js';
import {
  assertCallableSafeLetterTemplatePayload,
  cloneCallableSafeLetterTemplateJson,
  logLetterTemplateCreateDiagnostic,
} from '../../services/admin/letterTemplateCallableSafety.js';
import {
  adminProspectLetterTemplateErrorKeyFromError,
  classifyAdminCallableError,
} from './adminProspectLetterTemplateDisplay.js';
import LetterTemplatePreview from './letterTemplates/LetterTemplatePreview.jsx';
import LetterTemplatePdfPreviewButton from './letterTemplates/LetterTemplatePdfPreviewButton.jsx';
import LetterTemplateRichEditorShell from './letterTemplates/LetterTemplateRichEditorShell.jsx';
import {
  createEmptyLetterTemplateDocument,
  extractLetterTemplateAssetIds,
  validateLetterTemplateRichDraft,
} from './letterTemplates/letterTemplateDocument.js';
import { shouldBlockSaveForStaleTranslation } from './letterTemplates/letterTemplateTranslationState.js';
import { useLetterTemplateAssetPreviewUrls } from './letterTemplates/useLetterTemplateAssetPreviewUrls.js';

export default function AdminProspectLetterTemplateNewPage() {
  const { t } = useLanguage();
  const { impersonation } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [frenchDocument, setFrenchDocument] = useState(() => createEmptyLetterTemplateDocument());
  const [englishDocument, setEnglishDocument] = useState(null);
  const [bilingual, setBilingual] = useState(false);
  const [translation, setTranslation] = useState(null);
  const [translationSourceSnapshot, setTranslationSourceSnapshot] = useState(null);
  const [saving, setSaving] = useState(false);
  const [errorKey, setErrorKey] = useState(null);
  const [retryable, setRetryable] = useState(false);
  const [mobileTab, setMobileTab] = useState('edit');
  const createAttemptRef = useRef(createAdminProspectLetterTemplateWriteAttempt());
  const submitLockRef = useRef(false);

  useEffect(() => {
    createAttemptRef.current.reset();
  }, []);

  const impersonating = impersonation?.active === true;
  const translationStale = shouldBlockSaveForStaleTranslation(
    translation,
    frenchDocument,
    translationSourceSnapshot,
  );
  const draftValidation = validateLetterTemplateRichDraft({
    bilingual,
    frenchDocument,
    englishDocument: bilingual ? englishDocument : null,
    translationStale,
  });
  const canSubmit =
    !impersonating &&
    !saving &&
    name.trim().length > 0 &&
    name.trim().length <= ADMIN_PROSPECT_LETTER_TEMPLATE_NAME_MAX &&
    draftValidation.valid;

  const { getPreviewState, requestPreview } = useLetterTemplateAssetPreviewUrls({
    fetchPreviewUrl: async (assetId) =>
      getAdminProspectLetterTemplateAssetPreviewUrl(
        buildGetAdminProspectLetterTemplateAssetPreviewUrlRequest({ assetId }),
      ),
    enabled: !impersonating,
  });

  const previewAssetIds = [
    ...extractLetterTemplateAssetIds(frenchDocument),
    ...(bilingual ? extractLetterTemplateAssetIds(englishDocument) : []),
  ];

  useEffect(() => {
    if (impersonating) return;
    for (const assetId of previewAssetIds) {
      requestPreview(assetId).catch(() => {});
    }
  }, [previewAssetIds, impersonating, requestPreview]);

  const getAssetPreviewUrl = (assetId) => {
    if (!assetId || impersonating) return null;
    const state = getPreviewState(assetId);
    return state.status === 'ready' ? state.url : null;
  };

  const handleCreate = async () => {
    if (!canSubmit || submitLockRef.current) return;
    submitLockRef.current = true;
    setSaving(true);
    setErrorKey(null);
    setRetryable(false);
    let stage = 'fingerprint';
    try {
      const safeFr = cloneCallableSafeLetterTemplateJson(frenchDocument);
      const safeEn = bilingual ? cloneCallableSafeLetterTemplateJson(englishDocument) : null;
      const documents = { fr: safeFr };
      if (bilingual && safeEn) documents.en = safeEn;

      stage = 'callable-safe-assert';
      const safety = assertCallableSafeLetterTemplatePayload({
        name: name.trim(),
        documents,
      });
      if (!safety.ok) {
        const err = new Error('letter_template_payload_not_callable_safe');
        err.code = 'letter_template_payload_not_callable_safe';
        throw err;
      }

      stage = 'resolve-attempt';
      const payload = createAttemptRef.current.resolveAttemptPayload({
        fingerprint: JSON.stringify({ name: name.trim(), bilingual, documents }),
        build: (operationId) => {
          stage = 'build-request';
          const request = buildCreateAdminProspectLetterTemplateRichRequest({
            operationId,
            name: name.trim(),
            bilingual,
            documents,
          });
          if (translation && !translationStale) {
            request.translation = translation;
          }
          return request;
        },
      });

      stage = 'callable-create';
      const result = await createAdminProspectLetterTemplate(payload);
      createAttemptRef.current.discard();

      stage = 'response-validation';
      const templateId = result?.templateId;
      if (!templateId) throw new Error('missing-template-id');
      navigate(`/admin/prospection/letter-templates/${templateId}`);
    } catch (err) {
      const safeReason =
        err?.code === 'letter_template_payload_not_serializable' ||
        err?.code === 'letter_template_payload_not_callable_safe'
          ? 'invalid_document'
          : err?.details?.reason || null;
      logLetterTemplateCreateDiagnostic({ stage, error: err, safeReason });
      const kind = classifyAdminCallableError(err);
      setErrorKey(adminProspectLetterTemplateErrorKeyFromError(err));
      setRetryable(kind === 'unavailable');
    } finally {
      setSaving(false);
      submitLockRef.current = false;
    }
  };

  return (
    <div className="mx-auto max-w-6xl space-y-4">
      <div className="flex items-start gap-2">
        <FilePlus2 className="mt-0.5 h-5 w-5 shrink-0 text-indigo-600" aria-hidden />
        <div>
          <h1 className="text-xl font-semibold text-slate-900">
            {t('admin.prospection.letterTemplate.new.title')}
          </h1>
          <p className="mt-1 text-sm text-slate-600">
            {t('admin.prospection.letterTemplate.new.subtitle')}
          </p>
        </div>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-4">
        <label className="block text-sm font-medium text-slate-700" htmlFor="letter-template-name">
          {t('admin.prospection.letterTemplate.field.name')}
        </label>
        <input
          id="letter-template-name"
          type="text"
          value={name}
          maxLength={ADMIN_PROSPECT_LETTER_TEMPLATE_NAME_MAX}
          onChange={(event) => {
            createAttemptRef.current.reset();
            setName(event.target.value);
          }}
          className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
        />
      </div>

      {impersonating ? (
        <p className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
          {t('admin.prospection.letterTemplate.impersonationBlocked')}
        </p>
      ) : null}

      <div className="flex gap-2 md:hidden">
        <button
          type="button"
          className={`rounded-lg px-3 py-1.5 text-sm ${mobileTab === 'edit' ? 'bg-indigo-600 text-white' : 'bg-slate-100'}`}
          onClick={() => setMobileTab('edit')}
        >
          {t('admin.prospection.letterTemplate.mobile.edit')}
        </button>
        <button
          type="button"
          className={`rounded-lg px-3 py-1.5 text-sm ${mobileTab === 'preview' ? 'bg-indigo-600 text-white' : 'bg-slate-100'}`}
          onClick={() => setMobileTab('preview')}
        >
          {t('admin.prospection.letterTemplate.mobile.preview')}
        </button>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <div className={mobileTab === 'preview' ? 'hidden lg:block' : ''}>
          <LetterTemplateRichEditorShell
            editorSessionKey="new"
            frenchDocument={frenchDocument}
            englishDocument={englishDocument}
            bilingual={bilingual}
            translation={translation}
            readOnly={impersonating}
            impersonating={impersonating}
            t={t}
            onFrenchDocumentChange={(doc) => {
              createAttemptRef.current.reset();
              setFrenchDocument(doc);
            }}
            onEnglishDocumentChange={(doc) => {
              createAttemptRef.current.reset();
              setEnglishDocument(doc);
            }}
            onBilingualChange={(next) => {
              createAttemptRef.current.reset();
              setBilingual(next);
              if (!next) setEnglishDocument(null);
            }}
            onTranslationChange={(meta, snapshot) => {
              setTranslation(meta);
              setTranslationSourceSnapshot(snapshot);
            }}
          />
        </div>
        <div className={mobileTab === 'edit' ? 'hidden lg:block' : ''}>
          <LetterTemplatePreview
            frenchDocument={frenchDocument}
            englishDocument={englishDocument}
            bilingual={bilingual}
            t={t}
            getAssetPreviewUrl={getAssetPreviewUrl}
          />
        </div>
      </div>

      {!draftValidation.valid ? (
        <p className="rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm text-rose-900" role="alert">
          {translationStale
            ? t('admin.prospection.letterTemplate.error.translationStaleSave')
            : t('admin.prospection.letterTemplate.error.invalidDocument')}
        </p>
      ) : null}

      {errorKey ? (
        <div className="rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm text-rose-900" role="alert">
          <p>{t(errorKey)}</p>
          {retryable ? (
            <button
              type="button"
              className="mt-2 font-medium text-rose-800 underline"
              onClick={handleCreate}
            >
              {t('admin.prospection.letterTemplate.action.retry')}
            </button>
          ) : null}
        </div>
      ) : null}

      <LetterTemplatePdfPreviewButton
        t={t}
        impersonating={impersonating}
        bilingual={bilingual}
        frenchDocument={frenchDocument}
        englishDocument={englishDocument}
        translation={translation}
        translationStale={translationStale}
        draftValid={draftValidation.valid}
      />

      <button
        type="button"
        onClick={handleCreate}
        disabled={!canSubmit}
        className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-50"
      >
        {saving
          ? t('admin.prospection.letterTemplate.new.saving')
          : t('admin.prospection.letterTemplate.new.submit')}
      </button>
    </div>
  );
}
